/**
 * The rules of the onboarding sequence — which steps may be left, what the
 * summary says, and whether an answer is storable.
 *
 * ⚠️⚠️ NO `Date` AND NO CLOCK IN THIS MODULE, ON PURPOSE. "Today" is passed in
 * by the caller, exactly as in `utils/menstrualCycle.ts` and for the same
 * reason: a date comparison that reads the clock is green in CI and wrong for
 * users east of UTC, which is the class of bug that cannot be found by testing
 * harder. `onboardingFlow.noDate.test.ts` asserts the absence.
 */

import {
  CONSENTS,
  REASONS,
  type ConsentKind,
  type OnboardingStepKey,
} from '../constants/onboarding';

/** The city chosen in onboarding — the app's own `UserLocation`, plus what it is called. */
export interface OnboardingCity {
  label: string;
  lat: number;
  lng: number;
  timeZone: string;
  /** ISO country code, kept for the console's analysis view. */
  country?: string;
}

export interface OnboardingAnswers {
  reasons: string[];
  /** Becomes `user_profiles.name`. */
  preferredName: string;
  fullName: string;
  /** `YYYY-MM-DD`, or `''` while unanswered. */
  dateOfBirth: string;
  /**
   * ⭐⭐ REQUIRED since 2026-09-18 (owner): every new account starts with a
   * city. It powers the day clock and prayer times, and the console reads
   * country/city for its analysis view. `null` while unanswered.
   */
  city: OnboardingCity | null;
  /** ⚠️ `null` means UNANSWERED and must survive as null. */
  gender: 'male' | 'female' | null;
  consents: Record<ConsentKind, boolean>;
}

// ── Advancing ───────────────────────────────────────────────────────────────

/**
 * Whether the sequence may move on from `step`.
 *
 * ⚠️⚠️ `about` NOW GATES ON THREE THINGS (owner, 2026-09-18): the name, a
 * plausible birthday and a city. Birthday and city used to be skippable, and
 * the result was accounts with no age and no place — the two facts the business
 * view is built on. Identity stays optional, and unanswered stays unanswered.
 * @param today `YYYY-MM-DD` — never read from a clock in this module.
 */
export function canAdvance(step: OnboardingStepKey, a: OnboardingAnswers, today?: string): boolean {
  switch (step) {
    case 'motivation':
      return a.reasons.length > 0;
    case 'about':
      return a.preferredName.trim().length > 0
        && !!today && isDobPlausible(a.dateOfBirth, today)
        && !!a.city;
    case 'consents':
      return requiredConsentsGranted(a.consents);
    default:
      return true;
  }
}

/** What the About step is still waiting for — the button's tooltip, in order. */
export function aboutMissing(a: OnboardingAnswers, today: string): string[] {
  const missing: string[] = [];
  if (!a.preferredName.trim()) missing.push('your preferred name');
  if (!isDobPlausible(a.dateOfBirth, today)) missing.push('your date of birth');
  if (!a.city) missing.push('your city');
  return missing;
}

/** Every consent marked `required` in the catalog has been granted. */
export function requiredConsentsGranted(consents: Record<ConsentKind, boolean>): boolean {
  return CONSENTS.filter((c) => c.required).every((c) => consents[c.kind] === true);
}

// ── Date of birth ───────────────────────────────────────────────────────────

/**
 * A birth date that could belong to a living person: after 1900-01-01 and
 * strictly before today.
 *
 * ⚠️⚠️ THIS IS THE ONLY GUARD, so it has to be right. A draft of migration 0255
 * carried `date_of_birth` with a matching CHECK — until it turned out
 * `userStore.birthDate` already existed and was edited in Settings → Personal
 * Details. Storing it twice would have been two records of one fact, so the
 * column went; the cost is that the value now rides `user_identity.data`, a
 * JSON blob that cannot carry a constraint. Nothing behind this function will
 * catch a birth date in 1823.
 *
 * @param today `YYYY-MM-DD`, supplied by the caller. Never read from a clock here.
 */
export function isDobPlausible(iso: string, today: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  // ⭐ Lexicographic comparison is exact for zero-padded ISO dates, and needs
  // no parsing, no timezone and no Date.
  return iso > '1900-01-01' && iso < today;
}

/** An answer is storable when it is empty (skipped) or plausible. */
export const isDobStorable = (iso: string, today: string): boolean =>
  iso === '' || isDobPlausible(iso, today);

// ── Summary copy ────────────────────────────────────────────────────────────

/**
 * "You came here to build more structure, become more consistent, and explore
 * first." — reflected back on the summary step.
 *
 * ⚠️ Ordered by the CATALOG, not by the order they were tapped, so the same
 * selection always reads the same way.
 */
export function summarySentence(reasonKeys: readonly string[]): string {
  const picked = REASONS.filter((r) => reasonKeys.includes(r.key)).map((r) => r.short);
  if (picked.length === 0) return 'You have not told us what you are here for yet.';
  if (picked.length === 1) return `You came here to ${picked[0]}.`;
  if (picked.length === 2) return `You came here to ${picked[0]} and ${picked[1]}.`;
  return `You came here to ${picked.slice(0, -1).join(', ')}, and ${picked[picked.length - 1]}.`;
}

// ── Consent → effect ────────────────────────────────────────────────────────

/**
 * Whether declining the AI consent should switch every AI source off.
 *
 * ⭐⭐ THIS IS WHAT STOPS THE CONSENT BEING DECORATION. Recording "they said no"
 * in `user_consents` and then leaving the assistant reading everything is the
 * control-that-governs-nothing failure in its most damaging form — a consent
 * that is evidence of a promise the product did not keep. The ledger row is the
 * record; this is the effect.
 *
 * ⚠️ Deliberately asymmetric in what it means, not in what it returns: granting
 * maps to `allowed = true`, which `setAiVisibility` implements by DELETING the
 * key rather than writing `true`, so a source added in a later release is still
 * visible by default and nobody is silently degraded by this ever having run.
 */
export const aiSourcesAllowed = (consents: Record<ConsentKind, boolean>): boolean =>
  consents.ai === true;

/**
 * ⚠️⚠️ Whether to open the sequence on its own, for the signed-in user. Only once the
 * cloud has answered for THIS user (`checkedFor`, the store's `initializedUserId`):
 * the local flag starts false in a fresh browser, so deciding earlier showed a
 * returning user the whole sequence on every new device (live, 2026-10-03).
 * Settings → Replay opens it directly and does not come through here.
 */
export const shouldAutoOpenOnboarding = (
  userId: string | null | undefined,
  checkedFor: string | null,
  completed: boolean,
): boolean => !!userId && checkedFor === userId && !completed;
